import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import { Box, Paper } from '@mui/material';
import { AppLayout } from '@/components/layout/AppLayout';
import { DocsTree, DocViewer } from '@/components/app/docs';
import { getDocsTree, getDocContent } from '@/lib/api/docs';
import { useAuthHeader } from '@/hooks/useAuth';
import type { DocTreeNode } from '@/lib/types';

export function DocsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuthHeader();
  const [tree, setTree] = useState<DocTreeNode[]>([]);
  const [selectedPath, setSelectedPath] = useState<string | null>(searchParams.get('path'));
  const [content, setContent] = useState<string | null>(null);
  const [isLoadingTree, setIsLoadingTree] = useState(true);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ツリーを取得
  useEffect(() => {
    const fetchTree = async () => {
      try {
        setIsLoadingTree(true);
        setError(null);
        const authHeaders = getAuthHeader();
        const treeData = await getDocsTree(authHeaders);
        setTree(treeData);
      } catch (err) {
        console.error('ドキュメントツリーの取得に失敗しました:', err);
        setError(err instanceof Error ? err.message : 'ドキュメントツリーの取得に失敗しました');
      } finally {
        setIsLoadingTree(false);
      }
    };

    fetchTree();
  }, [getAuthHeader]);

  // 選択されたパスに基づいてコンテンツを取得
  useEffect(() => {
    const fetchContent = async () => {
      if (!selectedPath) {
        setContent(null);
        return;
      }

      try {
        setIsLoadingContent(true);
        setError(null);
        const authHeaders = getAuthHeader();
        const docContent = await getDocContent(selectedPath, authHeaders);
        setContent(docContent.content);
      } catch (err) {
        console.error('ドキュメント内容の取得に失敗しました:', err);
        setError(err instanceof Error ? err.message : 'ドキュメント内容の取得に失敗しました');
        setContent(null);
      } finally {
        setIsLoadingContent(false);
      }
    };

    fetchContent();
  }, [selectedPath, getAuthHeader]);

  // URLクエリパラメータと同期
  const handleSelect = (path: string) => {
    setSelectedPath(path);
    navigate(`/docs?path=${encodeURIComponent(path)}`);
  };

  return (
    <AppLayout title="ドキュメント">
      <Box
        sx={{
          display: 'flex',
          height: 'calc(100vh - 64px)', // AppBarの高さを考慮
          width: '100%',
        }}
      >
        {/* 左ペイン: ツリー */}
        <Paper
          elevation={1}
          sx={{
            width: '300px',
            minWidth: '300px',
            borderRight: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {isLoadingTree ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <p>読み込み中...</p>
            </Box>
          ) : error ? (
            <Box sx={{ p: 2 }}>
              <p style={{ color: 'error.main' }}>{error}</p>
            </Box>
          ) : (
            <DocsTree tree={tree} selectedPath={selectedPath} onSelect={handleSelect} />
          )}
        </Paper>

        {/* 右ペイン: コンテンツ */}
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DocViewer content={content} isLoading={isLoadingContent} error={error} />
        </Box>
      </Box>
    </AppLayout>
  );
}
